import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowsRotate,
	faCalendarCheck,
	faClock,
	faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import { Card, CardBody, Col, Row, UncontrolledTooltip } from "reactstrap";
import { getSyncStatus, getSyncTriggerKey } from "../../../Integrations/HiBob/messages";
import { count, describeNextSync, nextNightlySync, toMoment } from "./format";

export function SyncPill({ status }) {
	const { t } = useTranslation();
	const known = getSyncStatus(status);
	if (!known) {
		return null;
	}
	const tone = {
		success: "is-success",
		warning: "is-warning",
		danger: "is-danger",
		info: "is-running",
	}[known.color] || "is-neutral";
	return (
		<span className={`hibob-status is-small ${tone}`}>
			<span className="hibob-dot" aria-hidden="true" />
			{t(known.key)}
		</span>
	);
}

/** "12 min ago" with the absolute time as a tooltip (and for screen readers). */
export function RelativeTime({ id, value, formats }) {
	return (
		<>
			<time id={id} dateTime={value.toISOString()} tabIndex={0}>
				{formats.relative(value)}
				<span className="sr-only"> ({formats.dateTime(value)})</span>
			</time>
			<UncontrolledTooltip target={id}>{formats.dateTime(value)}</UncontrolledTooltip>
		</>
	);
}

function Tile({ testId, icon, label, value, sub }) {
	return (
		<Col xs="6" lg="3">
			<Card className="hibob-tile" data-testid={testId}>
				<CardBody>
					<div className="d-flex align-items-center">
						<span className="hibob-tile-icon mr-2" aria-hidden="true">
							<FontAwesomeIcon icon={icon} />
						</span>
						<h2 className="hibob-tile-label mb-0">{label}</h2>
					</div>
					<div className="hibob-tile-value">{value}</div>
					<div className="hibob-tile-sub">{sub}</div>
				</CardBody>
			</Card>
		</Col>
	);
}

export const usersStep = (sync) =>
	(Array.isArray(sync?.steps) ? sync.steps : []).find((step) => step?.step === "users") || null;

function SummaryTiles({ data, formats, onShowEmployeeIssues }) {
	const { t } = useTranslation();
	const sync = data.sync || {};
	const connected = data.status === "connected";
	const running = sync.lastStatus === "running";
	const firstPending = connected && !!sync.firstSyncPending && !running;
	const connectedAt = toMoment(data.connectedAt);
	const startedAt = toMoment(sync.lastStartedAt);
	const triggerKey = getSyncTriggerKey(sync.lastTrigger);
	const users = usersStep(sync);
	const inSync = users ? count(users.created) + count(users.updated) + count(users.unchanged) : null;
	const attention = users ? count(users.failed) + count(users.flagged) : 0;

	let lastValue;
	if (running) {
		lastValue = (
			<span>
				<FontAwesomeIcon icon={faArrowsRotate} spin className="mr-2" aria-hidden="true" />
				{t("hibob-syncing")}
			</span>
		);
	} else if (firstPending) {
		lastValue = t("hibob-first-sync-short");
	} else if (startedAt) {
		lastValue = <RelativeTime id="hibob-last-sync-time" value={startedAt} formats={formats} />;
	} else {
		lastValue = t("hibob-tile-not-yet");
	}

	let employeesValue;
	let employeesSub;
	if (firstPending) {
		employeesValue = t("hibob-first-sync-short");
		employeesSub = null;
	} else if (inSync === null) {
		employeesValue = "—";
		employeesSub = null;
	} else {
		employeesValue = <span className="hibob-num">{inSync}</span>;
		employeesSub =
			attention > 0 ? (
				<button type="button" className="hibob-link-button" onClick={onShowEmployeeIssues}>
					{t("hibob-tile-need-attention", { count: attention })}
				</button>
			) : (
				t("hibob-tile-all-up-to-date")
			);
	}

	const now = moment();
	const next = nextNightlySync(now);

	return (
		<Row>
			<Tile
				testId="hibob-tile-connected"
				icon={faCalendarCheck}
				label={t("hibob-tile-connected-since")}
				value={connectedAt ? formats.date(connectedAt) : "—"}
				sub={data.connectedBy?.name ? t("hibob-tile-by", { name: data.connectedBy.name }) : null}
			/>
			<Tile
				testId="hibob-tile-last-sync"
				icon={faArrowsRotate}
				label={t("hibob-tile-last-sync")}
				value={lastValue}
				sub={
					startedAt && !firstPending ? (
						<span className="d-inline-flex align-items-center flex-wrap">
							{!running && <SyncPill status={sync.lastStatus} />}
							{triggerKey && <span className={running ? "" : "ml-2"}>{t(triggerKey)}</span>}
						</span>
					) : null
				}
			/>
			<Tile
				testId="hibob-tile-employees"
				icon={faUserCheck}
				label={t("hibob-tile-employees")}
				value={employeesValue}
				sub={employeesSub}
			/>
			<Tile
				testId="hibob-tile-next-sync"
				icon={faClock}
				label={t("hibob-tile-next-sync")}
				value={connected ? describeNextSync(next, now, t, formats) : "—"}
				sub={connected ? t("hibob-tile-nightly") : t("hibob-tile-paused")}
			/>
		</Row>
	);
}

export default SummaryTiles;
